/**
 * Bridges the image shape produced by `@corbe30/fortune-excel` to the one
 * `@fortune-sheet/react` renders.
 *
 * The converter stores a sheet's pictures as an object keyed by a generated id,
 * each entry carrying its position under `default`:
 *
 *   { image_abc: { src, default: { left, top, width, height }, crop, ... } }
 *
 * FortuneSheet expects a flat list and calls `.map` on it while rendering, so a
 * workbook with any embedded picture crashes the viewer unless the sheets are
 * reshaped first.
 */

/** One picture as FortuneSheet's image layer renders it. */
export type FortuneSheetImage = {
  id: string;
  src: string;
  left: number;
  top: number;
  width: number;
  height: number;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const toNumber = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const toFortuneSheetImage = (
  id: string,
  raw: unknown,
): FortuneSheetImage | null => {
  if (!isRecord(raw) || typeof raw.src !== "string") return null;
  // Converter entries nest the box under `default`; already-flat entries
  // carry it at the top level.
  const box = isRecord(raw.default) ? raw.default : raw;
  return {
    id: typeof raw.id === "string" ? raw.id : id,
    src: raw.src,
    left: toNumber(box.left),
    top: toNumber(box.top),
    width: toNumber(box.width),
    height: toNumber(box.height),
  };
};

/**
 * Normalises a sheet's `images` value into FortuneSheet's list shape.
 * Entries without an image source are dropped rather than rendered broken.
 */
export const toFortuneSheetImages = (images: unknown): FortuneSheetImage[] => {
  const entries: [string, unknown][] = Array.isArray(images)
    ? images.map((image, index) => [`image_${index}`, image])
    : isRecord(images)
      ? Object.entries(images)
      : [];
  return entries
    .map(([id, raw]) => toFortuneSheetImage(id, raw))
    .filter((image): image is FortuneSheetImage => image !== null);
};

/** Returns the sheets with every `images` value in FortuneSheet's list shape. */
export const withFortuneSheetImages = <Sheet extends { images?: unknown }>(
  sheets: Sheet[],
): Sheet[] =>
  sheets.map((sheet) =>
    sheet.images == null
      ? sheet
      : { ...sheet, images: toFortuneSheetImages(sheet.images) },
  );
